import React, { memo, useEffect, useMemo } from 'react';

import produce from 'immer';
import { orderBy } from 'lodash';
import { Controller, UseFormReturn } from 'react-hook-form';

import { useQueryState } from 'hooks/useQueryState';

import { useGlobalFilterDataStore } from 'store/globalFilterData.store';

import { useQueryGetTagsAssignedToCurrentCustomerSubPrograms } from 'api/queries/getTagsAssignedToCurrentCustomerSubPrograms.query';

import { IState } from 'components/common/GlobalFilterComponent/GlobalFilterComponent';
import classes from 'components/common/GlobalFilterComponent/GlobalFilterComponent.module.scss';
import { FilterLabels } from 'components/common/GlobalFilterComponent/GlobalFilterComponent.utils';
import { listStyleWithExclude } from 'components/common/MyCustomSelect/myCustomSelect.style';

import { Select } from 'components/DesignSystem/Select';

interface IProps {
    methods?: UseFormReturn<any, any>;
    name: string;
    setState?: React.Dispatch<React.SetStateAction<IState>>;
    handleFilters?: () => void;
    skipFormSet?: boolean;
}

export const ProgramTagsFilter = memo(({ methods, name, setState, handleFilters, skipFormSet = false }: IProps) => {
    const [queryStringValue] = useQueryState('programTags');

    const { getValueFromStore } = useGlobalFilterDataStore();
    const storeValue = getValueFromStore<ISelectOption<string>[]>('programTags');

    const { data } = useQueryGetTagsAssignedToCurrentCustomerSubPrograms();

    const options = useMemo<ISelectOption<string>[]>(
        () =>
            data
                ? orderBy(
                      data.map((item) => ({ label: item.name, value: item.name })),
                      [(item: any) => item['label'].toLowerCase()],
                      ['asc'],
                  )
                : [],
        [data],
    );

    const isValue = useMemo(() => {
        if (queryStringValue && queryStringValue.length > 0) {
            return queryStringValue;
        } else {
            return storeValue;
        }
    }, [queryStringValue, storeValue]);

    useEffect(() => {
        if (setState) {
            if (!isValue || isValue.length === 0) {
                setState((prev) => {
                    return produce(prev, (draft) => {
                        draft.programTags.isReady = true;
                        draft.programTags.isEmpty = true;
                        return draft;
                    });
                });
            }

            if (isValue && isValue.length > 0 && data && !skipFormSet) {
                const convertStringToBoolean = isValue.map((item) => {
                    return {
                        ...item,
                        value: item.value,
                    };
                });
                methods?.setValue(name, convertStringToBoolean);

                setState((prev) => {
                    return produce(prev, (draft) => {
                        draft.programTags.isReady = true;
                        return draft;
                    });
                });
            }
        }
    }, [isValue, data, setState, skipFormSet]);

    const handleChange = (value: readonly ISelectOption<string>[]) => {
        methods?.setValue(name, value);
        handleFilters && handleFilters();
    };

    const handleValue = (value) => {
        return orderBy(value, (o) => o.value.toLowerCase());
    };

    return (
        <div className={classes.selectWrapper}>
            <div data-testid={'program-tags-filter'}>
                <Controller
                    render={({ field }) => (
                        <Select<ISelectOption<string>, true>
                            {...field}
                            options={options}
                            isMulti
                            onChange={handleChange}
                            value={handleValue(field.value)}
                            placeholder={'Select Tags...'}
                            label={FilterLabels.TAG_PORTFOLIO_BOARD}
                        />
                    )}
                    name={name}
                    control={methods?.control}
                />
            </div>
        </div>
    );
});
